import {
  MAX_LOCATIONS_PER_LOCATION_GROUP,
  MAX_LOCATION_GROUP_NAME_LENGTH
} from "../../@types/sharedSchema";
import type { LocationGroup, ProcessedListingAddress } from "../../@types/sharedSchema";
import { getListingAddressString } from "../Haim/utils";

/**
 * Location groups, in their entirety, as pure data transforms.
 *
 * ── The one idea everything here rests on ────────────────────────────────────
 *
 * A group is a UI SHORTHAND, never a search primitive. Picking one is EXPANDED
 * at pick time into its member locations (`expandLocationGroup`), so every layer
 * downstream — the search-request builders, the alert matcher, the contact
 * document, the CRM write path — keeps receiving a plain
 * `ProcessedListingAddress[]` and nothing has to learn what a group is. There is
 * no "group" branch to add anywhere, and no migration owed to data written
 * before groups existed.
 *
 * The price of that is `fromGroupId` / `fromGroupName`, two DISPLAY-ONLY fields
 * riding on the expanded members so the picker can collapse N members back into
 * one chip (`buildSelectionChips`) and so a re-opened contact still names the
 * group its areas were filled from.
 *
 * Those two fields cannot reach a query, and the reason is structural rather
 * than a convention anyone has to remember: `algoliaClient.buildPgSearchRequest`
 * (:171-184) and `matchesSearch.buildMatchSearchRequest` (:106-120) each build a
 * wire location by NAMING its five fields explicitly — city, neighborhood,
 * street, canonicalLocationId, canonicalLevel. Nothing spreads a picked location
 * onto the wire. Replace either of those with a spread and the guarantee dies
 * with it.
 *
 * ── Why identity is a KEY and not object identity ────────────────────────────
 *
 * The picker's existing duplicate test is `locations.includes(location)`, i.e.
 * reference equality, which cannot catch a repeat that arrived as a different
 * object. That was survivable while every pick came from a fresh autocomplete
 * response. It stops being survivable the moment two groups share an area, or a
 * group member equals a hand-picked location: the selection would hold the same
 * place twice, and `getListingAddressString` is both the LABEL and the React KEY
 * of a committed chip (`SearchStateFilterChips.tsx:212`,
 * `ListingsSearchArea.tsx:506`) — duplicate keys, and a filter that says the
 * same thing twice. Hence `locationIdentityKey`, used for every dedupe and for
 * every key this file hands out.
 */

/** What a picked selection renders as: plain locations, plus one chip per picked group. */
export type SelectionChip =
  | { kind: "location"; key: string; label: string; location: ProcessedListingAddress }
  | {
      kind: "group";
      key: string;
      /**
       * Same string as `name`. Present on both arms of the union so a chip row can
       * render `chip.label` without narrowing first — the two kinds differ in what
       * they DO, not in how they are titled.
       */
      label: string;
      groupId: string;
      name: string;
      emoji?: string;
      locations: ProcessedListingAddress[];
    };

/**
 * The value identity of a location — a canonical master id when the pick came
 * from autocomplete, otherwise its address text.
 *
 * `undefined` renders as `''` deliberately: absent and empty mean the same thing
 * for a location field (the picker keeps absent fields absent so a suggestion
 * lands in the right group), so a city-only pick must key identically whether it
 * was built with conditional spreads or read back off a document.
 */
export function locationIdentityKey(loc: ProcessedListingAddress): string {
  if (!loc) return "";
  if (loc.canonicalLocationId) return loc.canonicalLocationId;
  return `${loc.city ?? ""}|${loc.neighborhood ?? ""}|${loc.street ?? ""}`;
}

/** First occurrence wins, order preserved — the user's own pick order is the order they read. */
export function dedupeLocations(locations: ProcessedListingAddress[]): ProcessedListingAddress[] {
  const seen = new Set<string>();
  const out: ProcessedListingAddress[] = [];

  for (const location of locations ?? []) {
    const key = locationIdentityKey(location);
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(location);
  }

  return out;
}

/**
 * A group, as the selection it stands for. The provenance stamp is what lets the
 * members be collapsed back into one chip; it is the only trace a group leaves.
 */
export function expandLocationGroup(group: LocationGroup): ProcessedListingAddress[] {
  if (!group) return [];

  return (group.locations ?? []).map((location) => ({
    ...location,
    fromGroupId: group.id,
    fromGroupName: group.name
  }));
}

/**
 * Add a picked group to a selection that may already hold some of it.
 *
 * ── An area already picked by hand is ABSORBED, not skipped ──────────────────
 *
 * The obvious implementation skips every member whose place is already in the
 * selection, and it renders wrong: pick "כרמליה" by hand, then pick a group that
 * also contains it, and you get a group chip reading 1 next to a loose "כרמליה"
 * chip — a group that says it holds one area while its row in the dropdown says
 * two. Reported exactly that way.
 *
 * So an existing UNGROUPED pick of the same place is re-stamped to this group in
 * place: the loose chip disappears into the group chip, the count matches the
 * group, and the selection is unchanged as a set of places. It keeps its original
 * POSITION, which is what stops the chip row reshuffling — a group chip already
 * sits at the position of its first member.
 *
 * A place already stamped to ANOTHER group is left alone and counted as a
 * duplicate: it is on screen under that group's chip, and moving it would make
 * the other chip silently shrink instead.
 *
 * The counts are reported separately because they need different words: "already
 * in the list" is a no-op nobody has to act on, while "did not fit" means areas
 * the realtor asked for are genuinely missing.
 */
export function mergeGroupIntoSelection(
  current: ProcessedListingAddress[],
  group: LocationGroup,
  maxPicks: number
): {
  locations: ProcessedListingAddress[];
  added: number;
  absorbed: number;
  skippedDuplicates: number;
  skippedOverLimit: number;
} {
  const base = current ?? [];
  const members = expandLocationGroup(group);
  const memberByKey = new Map(members.map((member) => [locationIdentityKey(member), member]));

  // Pass 1 — adopt the loose picks this group names, in place.
  let absorbed = 0;
  const kept = base.map((location) => {
    if (location.fromGroupId) return location;

    const member = memberByKey.get(locationIdentityKey(location));
    if (!member) return location;

    absorbed += 1;
    return { ...location, fromGroupId: group.id, fromGroupName: group.name };
  });

  // Pass 2 — append what the selection does not hold yet. `seen` is seeded from
  // the post-adoption state and grown as we go, so a group listing the same place
  // twice collapses too, not just collisions against `current`.
  const seen = new Set(kept.map(locationIdentityKey));
  const fresh: ProcessedListingAddress[] = [];
  let skippedDuplicates = 0;

  for (const member of members) {
    const key = locationIdentityKey(member);
    if (seen.has(key)) {
      // Adopted above, or held by another group's chip. Only the latter is a
      // duplicate the realtor could be told about; an adoption is a success.
      if (!memberWasAbsorbed(kept, key, group.id)) skippedDuplicates += 1;
      continue;
    }
    seen.add(key);
    fresh.push(member);
  }

  const room = Math.max(0, (typeof maxPicks === "number" ? maxPicks : Infinity) - kept.length);
  const added = Math.min(fresh.length, room);

  return {
    locations: [...kept, ...fresh.slice(0, added)],
    added,
    absorbed,
    skippedDuplicates,
    skippedOverLimit: fresh.length - added
  };
}

/** Did this place end up under THIS group after the adoption pass? */
function memberWasAbsorbed(
  kept: ProcessedListingAddress[],
  key: string,
  groupId: string
): boolean {
  return kept.some(
    (location) => location.fromGroupId === groupId && locationIdentityKey(location) === key
  );
}

/**
 * Collapse a flat selection into what the chip row renders.
 *
 * A group chip takes the position of its FIRST member, so adding a group appends
 * one chip at the end and the ungrouped picks around it keep the order they were
 * picked in — a chip row that reshuffles on every change is unreadable.
 *
 * The name comes from the LIVE group when it still exists (so a rename shows up
 * everywhere at once) and falls back to the `fromGroupName` stamped at pick time
 * — which is the whole reason that field is stored beside the id: a contact's
 * saved areas must still name the group they came from after the group is gone.
 */
export function buildSelectionChips(
  locations: ProcessedListingAddress[],
  groups?: LocationGroup[]
): SelectionChip[] {
  const chips: SelectionChip[] = [];
  const chipIndexByGroupId = new Map<string, number>();

  for (const location of locations ?? []) {
    const groupId = location.fromGroupId;

    if (!groupId) {
      chips.push({
        kind: "location",
        key: `loc:${locationIdentityKey(location)}`,
        label: getListingAddressString(location),
        location
      });
      continue;
    }

    const existing = chipIndexByGroupId.get(groupId);
    if (existing !== undefined) {
      (chips[existing] as Extract<SelectionChip, { kind: "group" }>).locations.push(location);
      continue;
    }

    const live = (groups ?? []).find((g) => g?.id === groupId);
    const name = live?.name || location.fromGroupName || "";

    chipIndexByGroupId.set(groupId, chips.length);
    chips.push({
      kind: "group",
      key: `group:${groupId}`,
      label: name,
      groupId,
      name,
      ...(live?.emoji ? { emoji: live.emoji } : {}),
      locations: [location]
    });
  }

  return chips;
}

/**
 * Remove what one chip stands for.
 *
 * A group chip removes by `fromGroupId` rather than by the members it captured,
 * so it still clears the whole group if the selection changed under it. A
 * location chip only ever stands for an ungrouped pick (`buildSelectionChips`
 * routes every stamped member into a group chip), so it must not take a group
 * member that happens to name the same place with it.
 *
 * ── Why `groups` is a parameter, and what it repairs ─────────────────────────
 *
 * The stamp holds ONE group id, and `mergeGroupIntoSelection` dedupes on
 * identity — so an area belonging to two groups is stamped by whichever was
 * picked FIRST and is never re-added by the second. Without the re-homing pass
 * below, deleting the first group's chip also deletes an area the second group
 * still names: "כל תל אביב" and "צפון ת״א" both contain נווה צדק, the realtor
 * removes the wide one, and the narrow chip silently drops from 4 to 3. If that
 * selection is then saved, the contact's `interestedInLocations` loses a
 * neighborhood the realtor believes is still covered, and no alert fires for it
 * again — visible only as a small count that used to read one higher.
 *
 * So a removed member whose place is ALSO named by another group still standing
 * in the selection is kept and re-stamped to that group, joining its chip. It is
 * appended rather than spliced in place: a group chip sits at the position of its
 * FIRST member, which is already among the survivors, so the chip row does not
 * move.
 *
 * `groups` is optional and its absence reproduces the old behaviour exactly —
 * the caller has to be able to answer "which groups contain this place", and only
 * a caller holding the live group list can.
 */
export function removeSelectionChip(
  locations: ProcessedListingAddress[],
  chip: SelectionChip,
  groups?: LocationGroup[]
): ProcessedListingAddress[] {
  const current = locations ?? [];
  if (!chip) return current;

  if (chip.kind === "group") {
    const survivors: ProcessedListingAddress[] = [];
    const removed: ProcessedListingAddress[] = [];

    for (const location of current) {
      (location.fromGroupId === chip.groupId ? removed : survivors).push(location);
    }

    if (!removed.length) return survivors;

    // Only a group that still HAS a chip can adopt anything: a group whose every
    // member was a duplicate never rendered a chip, so re-homing to it would put
    // areas on screen under a name the realtor never saw appear.
    const stillPicked = new Set(
      survivors.map((location) => location.fromGroupId).filter(Boolean) as string[]
    );
    if (!stillPicked.size) return survivors;

    const rehomed: ProcessedListingAddress[] = [];

    for (const location of removed) {
      const key = locationIdentityKey(location);
      const owner = (groups ?? []).find(
        (candidate) =>
          candidate &&
          candidate.id !== chip.groupId &&
          stillPicked.has(candidate.id) &&
          (candidate.locations ?? []).some((member) => locationIdentityKey(member) === key)
      );

      if (!owner) continue;
      rehomed.push({ ...location, fromGroupId: owner.id, fromGroupName: owner.name });
    }

    return rehomed.length ? [...survivors, ...rehomed] : survivors;
  }

  const key = locationIdentityKey(chip.location);
  return current.filter(
    (location) => location.fromGroupId || locationIdentityKey(location) !== key
  );
}

/**
 * "רמת אביב, הצפון הישן, נווה צדק +4" — enough to recognise a group at a glance.
 *
 * Deliberately NOT `getListingAddressString`, which is the chip LABEL and spells
 * a location in full. A group is almost always one city's worth of areas, so the
 * full form repeats "תל אביב יפו" after every entry and a one-line summary
 * becomes a wall the eye slides off; the group's own name already carries that
 * context. So each entry is named by its most specific part.
 */
export function summarizeGroupLocations(group: LocationGroup, max: number = 3): string {
  const locations = group?.locations ?? [];
  const shown = locations
    .slice(0, max)
    .map((location) => location?.street || location?.neighborhood || location?.city || "")
    .filter(Boolean);
  const rest = locations.length - shown.length;

  if (!shown.length) return "";
  return rest > 0 ? `${shown.join(", ")} +${rest}` : shown.join(", ");
}

/**
 * Drop the group stamp. **Every path that PERSISTS locations must call this.**
 *
 * Provenance is scoped to a live selection, not to stored data, and the server
 * already treats it that way: `asInterestedInLocations` in the CRM write path
 * (`contactWriteRules.ts`) rebuilds each entry from named fields, so anything
 * saved through `POST /api/v1/contacts/update` loses the stamp regardless. The
 * client-side writers have to agree, or the same contact renders as one group
 * chip or as twelve plain ones depending on which surface last saved it.
 *
 * It is also the honest shape. A group is a snapshot taken at pick time — editing
 * the group later does not change a contact whose areas came from it — so a stored
 * "צפון ת״א" would imply a live link that does not exist. Twelve areas is what the
 * contact is actually interested in.
 */
export function stripGroupProvenance(location: ProcessedListingAddress): ProcessedListingAddress {
  if (!location?.fromGroupId && !location?.fromGroupName) return location;

  const { fromGroupId, fromGroupName, ...rest } = location;
  return rest as ProcessedListingAddress;
}

/** `stripGroupProvenance` over a whole selection — the shape every write wants. */
export function toPersistedLocations(locations: ProcessedListingAddress[]): ProcessedListingAddress[] {
  return (locations ?? []).map(stripGroupProvenance);
}

/**
 * The caps, checked before a write rather than after. Returns a reason rather
 * than a message: the caller owns the i18n key, this file owns the rule.
 */
export function validateLocationGroup(input: {
  name: string;
  locations: ProcessedListingAddress[];
}):
  | { ok: true }
  | { ok: false; reason: "name-empty" | "name-too-long" | "no-locations" | "too-many-locations" } {
  const name = (input?.name ?? "").trim();
  const locations = input?.locations ?? [];

  if (!name) return { ok: false, reason: "name-empty" };
  if (name.length > MAX_LOCATION_GROUP_NAME_LENGTH) return { ok: false, reason: "name-too-long" };
  if (!locations.length) return { ok: false, reason: "no-locations" };
  if (locations.length > MAX_LOCATIONS_PER_LOCATION_GROUP) {
    return { ok: false, reason: "too-many-locations" };
  }

  return { ok: true };
}
